import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, HandCoins, Landmark, Plus } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { FinanceEntrySummary } from '@aeris/contracts'
import { AGEING_BUCKET_LABELS, parseAmount } from '@aeris/finance'
import { formatMoney, isPositive } from '@aeris/money'
import {
  Button,
  CheckboxField,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  RowActions,
  RowOpener,
  SearchInput,
  Select,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  SortableHeaderCell,
  TableHeaderCell,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  VisuallyHidden,
} from '@aeris/ui'

import { ExportMenu } from '../components/export-menu.tsx'
import { FinanceEntryDetailDialog } from '../components/finance-entry-detail-dialog.tsx'
import { FinanceEntryForm } from '../components/finance-entry-form.tsx'
import { FinanceSettleDialog } from '../components/finance-settle-dialog.tsx'
import { PartyPicker } from '../components/party-picker.tsx'
import { DocumentStatusBadge } from '../components/status-badge.tsx'
import { StatusLegend } from '../components/status-legend.tsx'
import {
  ApiError,
  cancelFinanceEntry,
  fetchAgeing,
  fetchCurrentCashSession,
  fetchFinanceEntries,
  fetchFinanceEntry,
  fetchPaymentMethods,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { formatDate } from '../lib/date-format.ts'
import { FINANCE_EXPORT, financeSlice } from '../lib/finance-export.ts'
import { STATUS_LABELS } from '../lib/finance-labels.ts'
import {
  FINANCE_LEGEND,
  FINANCE_STATUS_META,
  overdueMeta,
} from '../lib/finance-status-meta.tsx'
import { PERIOD_LABELS, periodRange } from '../lib/period-range.ts'
import { readRememberedTerminal } from '../lib/remembered-terminal.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import { useTableSort } from '../lib/use-table-sort.ts'
import { useWorkspace } from '../lib/workspace.ts'

/**
 * Financeiro.
 *
 * One screen for both directions rather than two, switched at the top. Contas a
 * receber and contas a pagar are the same object seen from opposite sides —
 * same columns, same baixa, same ageing — and two screens would be one
 * component copied with a word changed, which is the sort of duplication that
 * drifts until the two disagree about what "vencido" means.
 *
 * The ageing strip is above the list on purpose. "Quanto eu tenho a receber?"
 * is the wrong question and everybody asks the right one instead: how old is
 * it. R$ 40.000,00 due next week and R$ 40.000,00 ninety days late are the same
 * total and completely different businesses.
 */

const PAGE_SIZE = 25

/** A exportação caminha de 100 em 100: menos pedidos, e cada um ainda pequeno. */
const EXPORT_PAGE_SIZE = 100

type Direction = 'receivable' | 'payable'

const DIRECTION_LABELS: Readonly<Record<Direction, string>> = {
  receivable: 'A receber',
  payable: 'A pagar',
}

export const FinanceiroPage = (): ReactNode => {
  const { allows, session: authSession } = useAuth()
  const workspace = useWorkspace()

  /**
   * O caixa aberto deste terminal (ADR-0021, decisão 9).
   *
   * Uma baixa em dinheiro lança na gaveta, então a tela precisa saber qual. O
   * mesmo terminal lembrado do PDV e do caixa, porque é a mesma máquina.
   */
  const till = useQuery({
    queryKey: [
      'cash-session',
      {
        terminalId: readRememberedTerminal(),
        branchId: authSession?.context?.branchId ?? undefined,
      },
    ],
    queryFn: ({ signal }) =>
      fetchCurrentCashSession(
        {
          terminalId: readRememberedTerminal(),
          branchId: authSession?.context?.branchId ?? undefined,
        },
        signal
      ),
    enabled: readRememberedTerminal() !== '',
  })
  const queryClient = useQueryClient()

  const [direction, setDirection] = useState<Direction>('receivable')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'' | FinanceEntrySummary['status']>(
    'open'
  )
  const [overdueOnly, setOverdueOnly] = useState(false)
  /*
   * "O que vence hoje / nesta semana" e "tudo do João" (F132): a pergunta do
   * dia no financeiro, e a API já filtrava pelos dois.
   */
  const [due, setDue] = useState<'' | 'today' | 'next7' | 'month'>('')
  const [partyId, setPartyId] = useState('')
  const [page, setPage] = useState(1)

  // The list goes back to the first page when the column changes: a new
  // order with the old offset shows rows that have nothing to do with
  // what was just clicked.
  const sort = useTableSort<'due_at' | 'amount' | 'issued_at'>(
    'due_at',
    'asc',
    () => {
      setPage(1)
    }
  )
  const [formOpen, setFormOpen] = useState(false)
  const [settlingId, setSettlingId] = useState<string | null>(null)
  /** A ficha de leitura (F154): clicar na linha, sem tocar em nada. */
  const [viewingId, setViewingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const debouncedSearch = useDebounced(search)

  const canWrite = allows('finance.write')
  const canSettle = allows('finance.settle')

  /*
   * O filtro, montado uma vez — F158. A lista e a exportação leem daqui.
   *
   * Sem `offset`/`limit`: quem chama decide a fatia. E ATENÇÃO — esta rota
   * pagina por `offset`, não por `page`, ao contrário de todas as outras. A
   * exportação converte; errar isso escreveria a primeira página vinte vezes,
   * em silêncio, num arquivo que parece completo.
   */
  const listRequest = {
    direction,
    ...(debouncedSearch.trim().length > 0
      ? { search: debouncedSearch.trim() }
      : {}),
    ...(status === '' ? {} : { status }),
    ...(overdueOnly ? { overdueOnly: true } : {}),
    ...(due === ''
      ? {}
      : (() => {
          const range = periodRange(due)
          return { dueFrom: range.from, dueTo: range.to }
        })()),
    ...(partyId === '' ? {} : { partyId }),
    sort: sort.sort,
    // Finance calls it `order`; every other contract calls it `direction`.
    // Left visible rather than smoothed over.
    order: sort.direction,
  }

  const query = useQuery({
    queryKey: [
      'finance',
      'entries',
      {
        direction,
        search: debouncedSearch,
        status,
        overdueOnly,
        due,
        partyId,
        page,
        // Spelled out rather than spread: `direction` here already means
        // receivable-or-payable, and the sort's own direction would silently
        // overwrite it in the cache key.
        sort: sort.sort,
        order: sort.direction,
      },
    ],
    queryFn: ({ signal }) =>
      fetchFinanceEntries(
        { ...listRequest, ...financeSlice(page, PAGE_SIZE) },
        signal
      ),
    placeholderData: (previous) => previous,
  })

  const ageing = useQuery({
    queryKey: ['finance', 'ageing', direction],
    queryFn: ({ signal }) => fetchAgeing(direction, signal),
  })

  const methods = useQuery({
    queryKey: ['payment-methods'],
    queryFn: ({ signal }) => fetchPaymentMethods({ status: 'active' }, signal),
    enabled: canSettle,
  })

  const settling = useQuery({
    queryKey: ['finance', 'entry', settlingId],
    queryFn: ({ signal }) => fetchFinanceEntry(settlingId ?? '', signal),
    enabled: settlingId !== null,
  })

  /*
   * Cancelar pede confirmação e motivo (F132). O motivo fixo "Cancelado na
   * tela do financeiro" não dizia a ninguém por que o título sumiu.
   */
  const [cancelling, setCancelling] = useState<FinanceEntrySummary | null>(null)

  const cancel = useMutation({
    mutationFn: async (input: { entry: FinanceEntrySummary; reason: string }) =>
      await cancelFinanceEntry(input.entry.id, {
        version: input.entry.version,
        reason: input.reason,
      }),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['finance'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível cancelar o título.'
      )
    },
    onSettled: () => {
      setCancelling(null)
    },
  })

  const total = query.data?.total ?? 0
  const items = query.data?.items ?? []
  /*
   * O título que está sendo visto, tirado da própria lista (F154).
   *
   * É o que decide se a ficha oferece Baixar, e sai daqui de graça: a linha
   * clicada já está carregada.
   */
  const viewingEntry = items.find((item) => item.id === viewingId) ?? null
  // "Em aberto" é o padrão da tela, não um filtro que a pessoa ligou.
  const filtered =
    debouncedSearch.trim().length > 0 ||
    status !== 'open' ||
    overdueOnly ||
    due !== '' ||
    partyId !== ''

  return (
    <Page>
      <PageHeader
        title="Financeiro"
        description="O que a loja tem a receber e o que deve, com vencimento e baixa."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* O "?" da barra: as situações de um título, incluindo o atraso,
                que aparece no lugar de uma situação sem ser uma (F156). */}
            <StatusLegend
              entries={FINANCE_LEGEND}
              label="as situações de um título"
            />
            {canWrite ? (
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  setFormOpen(true)
                }}
              >
                Novo título
              </Button>
            ) : null}
          </div>
        }
      />

      {/* Two directions, one screen: the same object seen from opposite
          sides. As abas do kit (F132) trazem as setas do teclado que a trilha
          feita à mão não tinha. */}
      <Tabs
        value={direction}
        onValueChange={(value) => {
          setDirection(value as Direction)
          setPage(1)
        }}
      >
        <TabsList aria-label="Direção" className="flex w-full">
          {(['receivable', 'payable'] as const).map((value) => (
            <TabsTrigger key={value} value={value} className="flex-1">
              {DIRECTION_LABELS[value]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/*
        Os vencimentos. Enquanto carrega, e quando não carrega, os cartões
        dizem isso — um cartão que some é um "não há nada vencido" que ninguém
        afirmou.
      */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {ageing.data === undefined
          ? (
              Object.keys(
                AGEING_BUCKET_LABELS
              ) as (keyof typeof AGEING_BUCKET_LABELS)[]
            ).map((bucket) => (
              <StatTile
                key={bucket}
                label={AGEING_BUCKET_LABELS[bucket]}
                value="—"
                loading={ageing.isPending}
                error={ageing.isError}
              />
            ))
          : ageing.data.buckets.map((bucket) => (
              <StatTile
                key={bucket.bucket}
                label={AGEING_BUCKET_LABELS[bucket.bucket]}
                value={
                  <span
                    className={`font-(--font-mono) text-(length:--text-base) ${
                      bucket.bucket === 'current'
                        ? 'text-(--text-primary)'
                        : 'text-(--danger)'
                    }`}
                  >
                    {formatMoney(parseAmount(bucket.amount))}
                  </span>
                }
                hint={
                  bucket.count === 1
                    ? '1 título'
                    : `${String(bucket.count)} títulos`
                }
              />
            ))}
      </div>

      <ListToolbar>
        <SearchInput
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setPage(1)
          }}
          placeholder="Buscar por descrição, documento ou cadastro"
          aria-label="Buscar títulos"
        />

        <Select
          value={status}
          aria-label="Situação"
          className="w-44"
          onChange={(event) => {
            setStatus(event.target.value as '' | FinanceEntrySummary['status'])
            setPage(1)
          }}
        >
          <option value="">Todas as situações</option>
          {(Object.keys(STATUS_LABELS) as FinanceEntrySummary['status'][]).map(
            (value) => (
              <option key={value} value={value}>
                {STATUS_LABELS[value]}
              </option>
            )
          )}
        </Select>

        <Select
          value={due}
          aria-label="Vencimento"
          className="w-44"
          onChange={(event) => {
            setDue(event.target.value as '' | 'today' | 'next7' | 'month')
            setPage(1)
          }}
        >
          <option value="">Qualquer vencimento</option>
          <option value="today">Vence hoje</option>
          <option value="next7">{PERIOD_LABELS.next7}</option>
          <option value="month">
            Vence {PERIOD_LABELS.month.toLowerCase()}
          </option>
        </Select>

        <PartyPicker
          value={partyId}
          placeholder={direction === 'receivable' ? 'Cliente' : 'Fornecedor'}
          aria-label="Pessoa"
          className="w-56"
          onChange={(value) => {
            setPartyId(value)
            setPage(1)
          }}
        />

        <CheckboxField
          label="Só vencidos"
          checked={overdueOnly}
          onCheckedChange={(checked) => {
            setOverdueOnly(checked === true)
            setPage(1)
          }}
          className="self-center"
        />

        {/*
          F158. `exportPage` é 1-based, como em toda lista; esta rota pagina por
          `offset`. `financeSlice` é quem sabe converter, e tem teste.
        */}
        <ExportMenu
          fileName={direction === 'receivable' ? 'a-receber' : 'a-pagar'}
          sheetName={direction === 'receivable' ? 'A receber' : 'A pagar'}
          columns={FINANCE_EXPORT}
          fetchPage={async (exportPage) =>
            await fetchFinanceEntries({
              ...listRequest,
              ...financeSlice(exportPage, EXPORT_PAGE_SIZE),
            })
          }
          pageSize={EXPORT_PAGE_SIZE}
          total={total}
        />
      </ListToolbar>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <ResultCount
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          loading={query.isPending}
          none="Nenhum título encontrado"
        />
        {query.data === undefined ? null : (
          <p className="text-(length:--text-xs) text-(--text-muted)">
            {/* The whole filtered set, not this page. A total that changes when
                somebody turns the page ends a shopkeeper's trust in a report. */}
            Em aberto no filtro:{' '}
            <strong className="font-(--font-mono) text-(--text-primary)">
              {formatMoney(parseAmount(query.data.outstandingTotal))}
            </strong>
          </p>
        )}
      </div>

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
        what="os títulos"
        isEmpty={items.length === 0}
        filtered={filtered}
        onClearFilters={() => {
          setSearch('')
          setDue('')
          setPartyId('')
          setStatus('open')
          setOverdueOnly(false)
          setPage(1)
        }}
        empty={
          <EmptyState
            icon={<Landmark aria-hidden="true" className="h-8 w-8" />}
            title={
              direction === 'receivable'
                ? 'Nada em aberto a receber'
                : 'Nada em aberto a pagar'
            }
            description={
              canWrite
                ? 'Registre um título, ou receba uma compra: o recebimento gera as parcelas a pagar sozinho.'
                : 'Você não tem permissão para registrar títulos.'
            }
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeaderCell kind="date" {...sort.column('due_at')}>
                  Vencimento
                </SortableHeaderCell>
                <TableHeaderCell>Descrição</TableHeaderCell>
                <TableHeaderCell>
                  {direction === 'receivable' ? 'Cliente' : 'Fornecedor'}
                </TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                <SortableHeaderCell numeric {...sort.column('amount')}>
                  Valor
                </SortableHeaderCell>
                <TableHeaderCell numeric>Em aberto</TableHeaderCell>
                {canWrite || canSettle ? (
                  <TableHeaderCell>
                    <VisuallyHidden>Ações</VisuallyHidden>
                  </TableHeaderCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((entry) => (
                /*
                 * Clicar na linha abre a FICHA (F154).
                 *
                 * Como em Compras, a coluna de ações só existe para quem pode
                 * escrever ou dar baixa — quem tem só `finance.read` não tinha
                 * clique nenhum nesta lista.
                 */
                <TableRow
                  key={entry.id}
                  onOpen={() => {
                    setViewingId(entry.id)
                  }}
                >
                  <TableCell className="whitespace-nowrap">
                    {formatDate(entry.dueAt)}
                  </TableCell>
                  <TableCell>
                    <RowOpener
                      onOpen={() => {
                        setViewingId(entry.id)
                      }}
                    >
                      {entry.description}
                    </RowOpener>
                    {/* A parcela: "2/3" responde "quantas faltam?" sem abrir. */}
                    {entry.instalmentCount > 1 ? (
                      <span className="ml-2 text-(length:--text-xs) text-(--text-muted)">
                        parcela {entry.instalmentNumber}/{entry.instalmentCount}
                      </span>
                    ) : null}
                    {entry.documentNumber === null ? null : (
                      <span className="ml-2 text-(length:--text-xs) text-(--text-muted)">
                        {entry.documentNumber}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{entry.partyName}</TableCell>
                  <TableCell>
                    {/*
                      O atraso aparece como NÚMERO DE DIAS, e não como situação:
                      é um fato sobre hoje, e o número é do que trata a ligação
                      de cobrança. A cor e a frase saem do registro, que é o
                      mesmo lido pela legenda e pela ficha (F156).
                    */}
                    <DocumentStatusBadge
                      meta={
                        entry.status === 'open' && entry.isOverdue
                          ? overdueMeta(entry.daysOverdue)
                          : FINANCE_STATUS_META[entry.status]
                      }
                    />
                  </TableCell>
                  <TableCell numeric>
                    {formatMoney(parseAmount(entry.amount))}
                  </TableCell>
                  <TableCell numeric>
                    {formatMoney(parseAmount(entry.outstanding))}
                  </TableCell>
                  {canWrite || canSettle ? (
                    <TableCell>
                      <RowActions
                        name={entry.description}
                        primary={
                          canSettle && entry.status !== 'cancelled' ? (
                            <Button
                              variant={
                                entry.status === 'open' ? 'primary' : 'ghost'
                              }
                              size="sm"
                              onClick={() => {
                                setSettlingId(entry.id)
                              }}
                            >
                              {/* "Baixar" e "Baixas" eram fáceis de trocar. */}
                              {entry.status === 'open'
                                ? 'Baixar'
                                : 'Ver baixas'}
                            </Button>
                          ) : undefined
                        }
                        items={[
                          // Cobranças (F135): do título ao cliente, com a
                          // fila, a promessa e o WhatsApp.
                          direction === 'receivable' &&
                            entry.status === 'open' && {
                              label: 'Cobrar',
                              icon: (
                                <HandCoins
                                  aria-hidden="true"
                                  className="h-4 w-4"
                                />
                              ),
                              onSelect: () => {
                                workspace.open({
                                  module: 'cobrancas',
                                  entityId: entry.partyId,
                                })
                              },
                            },
                          // Nada recebido (F349): a baixa estornada não
                          // conta — é a mesma regra do servidor.
                          canWrite &&
                            entry.status === 'open' &&
                            !isPositive(parseAmount(entry.settledAmount)) && {
                              label: 'Cancelar',
                              danger: true,
                              icon: (
                                <Ban aria-hidden="true" className="h-4 w-4" />
                              ),
                              onSelect: () => {
                                setCancelling(entry)
                              },
                            },
                        ]}
                      />
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <ConfirmDialog
        open={cancelling !== null}
        onOpenChange={(next) => {
          if (!next) setCancelling(null)
        }}
        title="Cancelar título"
        description={
          cancelling === null
            ? ''
            : `Cancelar "${cancelling.description}" de ${cancelling.partyName}, no valor de ${formatMoney(parseAmount(cancelling.amount))}? Ele sai do que a loja tem ${direction === 'receivable' ? 'a receber' : 'a pagar'}, e o cancelamento fica no histórico com o motivo.`
        }
        confirmLabel="Cancelar título"
        cancelLabel="Voltar"
        reason={{
          label: 'Motivo',
          placeholder: 'Ex.: lançado em duplicidade',
          required: true,
        }}
        loading={cancel.isPending}
        onConfirm={(reason) => {
          if (cancelling === null) return
          cancel.mutate({ entry: cancelling, reason: reason?.trim() ?? '' })
        }}
      />

      {formOpen ? (
        <FinanceEntryForm
          direction={direction}
          onClose={() => {
            setFormOpen(false)
          }}
        />
      ) : null}

      {/* Keyed by the title being settled, so the dialog starts from the right
          values without an effect that resets them. */}
      {settlingId !== null && settling.data !== undefined ? (
        <FinanceSettleDialog
          key={settlingId}
          entry={settling.data.entry}
          methods={methods.data?.methods ?? []}
          cashSessionId={till.data?.session?.id ?? null}
          onClose={() => {
            setSettlingId(null)
          }}
        />
      ) : null}

      {viewingId === null ? null : (
        <FinanceEntryDetailDialog
          entryId={viewingId}
          onClose={() => {
            setViewingId(null)
          }}
          /*
           * Não há "Editar": não existe formulário de edição de título. O que
           * se faz com um título é dar baixa, e quem pode é quem tem
           * `finance.settle` — a mesma regra do botão da linha.
           */
          {...(canSettle && viewingEntry?.status !== 'cancelled'
            ? {
                onSettle: () => {
                  setSettlingId(viewingId)
                  setViewingId(null)
                },
              }
            : {})}
        />
      )}
    </Page>
  )
}
